/**
 * Centralised i18n + color tokens for booking statuses.
 * Use everywhere status badges are shown so the UX is consistent.
 */
export const BOOKING_STATUS_IT = {
  pending: "In attesa",
  confirmed: "Confermata",
  checked_in: "Check-in fatto",
  completed: "Completata",
  cancelled: "Annullata",
  rejected: "Rifiutata",
  frozen: "Congelata",
  no_show: "Mancata presentazione",
};

// Color tokens per requested by host:
//   In attesa     → giallo acceso
//   Confermata    → verde
//   Check-in fatto→ giallo/arancio (walk-in dalla Guida o ospite arrivato)
//   Completata    → blu non troppo scuro
//   Annullata     → arancione (un po' più scuro)
//   Rifiutata     → rosso
//   Congelata     → bianco sfumato celeste chiarissimo
//   No-show       → grigio (non troppo chiaro)
export const BOOKING_STATUS_COLORS = {
  pending:    "bg-yellow-300 text-yellow-900 border-yellow-500",
  confirmed:  "bg-[#25D366] text-white border-[#1FBA56]",
  checked_in: "bg-amber-200 text-amber-900 border-amber-500",
  completed:  "bg-blue-200 text-blue-900 border-blue-400",
  cancelled:  "bg-orange-300 text-orange-900 border-orange-500",
  rejected:   "bg-rose-200 text-rose-900 border-rose-500",
  frozen:     "bg-sky-50 text-sky-800 border-sky-200",
  no_show:    "bg-slate-300 text-slate-800 border-slate-400",
};

export const labelFor = (status) => BOOKING_STATUS_IT[status] || status || "—";
export const colorFor = (status) => BOOKING_STATUS_COLORS[status] || "bg-slate-100 text-slate-700 border-slate-300";

/**
 * Legend component — small inline reference for booking-status colors.
 * Pass `statuses` to limit which ones are shown (defaults to all).
 */
import { Badge } from "@/components/ui/badge";

export function BookingStatusLegend({ statuses = null, compact = false, className = "", showWalkinDot = false, showEstimatedPrice = false }) {
  const keys = statuses || Object.keys(BOOKING_STATUS_IT);
  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 text-[10px] text-[#635F59] ${className}`}
      data-testid="booking-status-legend"
    >
      <span className="font-semibold uppercase tracking-wider">Legenda:</span>
      {keys.map((k) => (
        <Badge key={k} variant="outline" className={`${colorFor(k)} px-1.5 py-0 text-[10px]`}>
          {compact ? labelFor(k).slice(0, 4) : labelFor(k)}
        </Badge>
      ))}
      {showWalkinDot && (
        <span
          className="inline-flex items-center gap-1 pl-2 ml-1 border-l border-[#E8E2D9]"
          title="Prenotazione entrata dall'App Guida Digitale come walk-in. Sparisce quando classifichi la provenienza."
          data-testid="legend-walkin"
        >
          <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
          <span>Walk-in</span>
        </span>
      )}
      {showEstimatedPrice && (
        <span
          className="inline-flex items-center gap-1 pl-2 ml-1 border-l border-[#E8E2D9]"
          title="Prezzo stimato — Booking.com non manda il totale nelle email. Modifica manualmente per confermarlo."
          data-testid="legend-estimated"
        >
          <span className="text-amber-600 text-[11px] leading-none">⚠</span>
          <span>Prezzo da verificare</span>
        </span>
      )}
    </div>
  );
}
